<template>
	<span
		ref="editor"
		:data-key="datakey"
		class="fd-editable"
		:style="inputStyle"
		role="textbox"
		:tabindex="tabindex"
		@input="inputChanged"
		:contenteditable="canEdit">{{ modelValue }}</span>
</template>

<script setup lang="ts">
import {computed, type CSSProperties, ref, watch} from "vue";
import {getCaretPosition, setCaretPosition} from "@fd/editors/suggester/ContentEditableUtils.ts";

const props = withDefaults(defineProps<{
	disabled?: boolean;
	minWidth?: number;
	datakey?: string | number | object;
	placeholder?: string;
	tabindex?: number;
}>(), {
	disabled: false,
	minWidth: 2,
	placeholder: "",
	tabindex: 0
});

const text = defineModel<string>();

const editor = ref<HTMLSpanElement | null>(null);
const lastCaretPosition = ref<number>(0);
const lastContent = ref<string>(text.value);

const canEdit = computed(() => !props.disabled);

watch(text, (newVal) => {
	if (newVal === lastContent.value) {
		// modelValue was reactively set back
		setCaretPosition(editor.value, lastCaretPosition.value);
	}
}, {
	flush: "post"
});

const inputStyle = computed<CSSProperties>(() => {
	const s: CSSProperties = {};
	if (props.minWidth > 0) {
		s.minWidth = props.minWidth + "px";
	}
	if (props.placeholder && !text.value) {
		s.backgroundImage = "url(" + generateSvgUrl(props.placeholder, "#AAAAAA") + ")";
	}
	if (canEdit.value) {
		s.cursor = "text";
	}
	return s;
});

function inputChanged(event: Event) {
	lastCaretPosition.value = getCaretPosition(editor.value);
	const value = editor.value ? editor.value.innerText : "";
	lastContent.value = value;
	text.value = value;
}

function focus() {
	if (editor.value) {
		editor.value.focus();
	}
}

defineExpose({focus});

function generateSvgText(text: string, color: string): string {
	const svg = `<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="200px" height="18px" viewBox="0 0 200 18">
    <text x="2" y="14" fill="${color}" style="font-family:'Helvetica Neue',sans-serif;font-weight:300;font-size:14px">${text}</text>
    </svg>`;
	return svg;
}

function generateSvgUrl(text: string, color: string): string {
	const svg = generateSvgText(text, color);
	const encoded = window.btoa(svg);
	return "data:image/svg+xml;base64," + encoded
}
</script>

<style>
.fd-editable {
	box-sizing: border-box;
	border: none;
	padding: 0 2px;
	outline: none;
	opacity: 0.25;
	background-image: none;
	background-repeat: no-repeat;
	background-position: left center;
}

.fd-editable[contenteditable="true"] {
	opacity: 1;
}

.fd-editable[contenteditable="true"]:empty:not(:focus) {
}

/* Workaround Safari baseline alignment of empty elements */
.fd-editable:empty::before {
	content: '\200B'; /* This is &ZeroWidthSpace; entity */
}

.fd-editable:last-child {
	flex-grow: 1;
}
</style>
